import { useRef } from 'react';
import styled from 'styled-components';
import { spacing, textColors } from 'ui/theme';
import { TextInputField, TextAreaField } from 'modules/templateEditor/common/components';
import { useEditableFieldsContext } from 'modules/templateEditor/context/EditableFieldsContext';
import { InAppContentDefinition } from 'modules/graphql/types';
import { FormField } from 'ui/formField';
import { InputText } from 'ui/inputs';
import { OpenNewWindow16 } from 'ui/icons';
import { pxToRem } from 'utils/convert';
import { SETTINGS_DISPLAY_CONTENT } from '../constants';
import { constructFieldErrors, getIsEditMode } from '../../common/utils';
import { useQueryParamContext } from '../../context/QueryParamContext';
import { useErrorValidationContext } from '../../context/ErrorValidationContext';
import { SettingsReadOnlyText } from '../../common/EditorContentContainer.styled';
import { constructErrorInlineText } from '../../common/ErrorInlineText';
import { getMobileInboxRestrictionText } from '../utils';
import { FieldNames } from '../../common/types';

const StyledReadOnlyText = styled(SettingsReadOnlyText)`
  overflow-wrap: anywhere;
`;

const ReadOnlyContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const ReadOnlyWrapper = styled.div`
  padding-bottom: ${spacing.half};
`;

const ReadOnlyLink = styled.a`
  display: inline-flex;
  align-items: center;
  position: relative;
  gap: ${spacing.eighth};
  margin-left: ${spacing.quarter};
  padding-bottom: ${spacing.half};
  text-decoration: none;
`;

const LinkContent = styled.span<{ isUnderlined?: boolean }>`
  ${(props) => (props.isUnderlined ? `text-decoration: underline;` : '')}
  color: ${textColors.textLink};
  margin-left: ${spacing.half};
  max-width: ${pxToRem(200)};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const MobileInbox = () => {
  const { view } = useQueryParamContext();
  const { isEditMode, liveTemplate, setCurrentMediumField } = useEditableFieldsContext();
  const { localeContentErrors, localeContentWarnings } = useErrorValidationContext();
  const inAppData = (liveTemplate?.mediumData || {}) as InAppContentDefinition;
  const isEditModeVisible = getIsEditMode(view, isEditMode);
  const validateUrl = (str: string) => {
    const checkUrl = new RegExp('^(http|https)://', 'i');
    return checkUrl.test(str) ? str : `https://${str}`;
  };
  const titleRef = useRef<HTMLInputElement | null>(null);
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);

  const { hasError: hasMessageInboxTitleError, hasWarning: hasMessageInboxTitleWarning } =
    constructFieldErrors({
      contentErrors: localeContentErrors,
      contentWarnings: localeContentWarnings,
      fieldName: FieldNames.INBOX_METADATA_TITLE,
    });

  const { hasError: hasMessageInboxSubtitleError, hasWarning: hasMessageInboxSubtitleWarning } =
    constructFieldErrors({
      contentErrors: localeContentErrors,
      contentWarnings: localeContentWarnings,
      fieldName: FieldNames.INBOX_METADATA_SUBTITLE,
    });

  const { hasError: hasMessageInboxIconError, hasWarning: hasMessageInboxIconWarning } =
    constructFieldErrors({
      contentErrors: localeContentErrors,
      contentWarnings: localeContentWarnings,
      fieldName: FieldNames.INBOX_METADATA_ICON,
    });

  const messageInboxTitleErrorText = constructErrorInlineText({
    condition: isEditModeVisible,
    hasError: hasMessageInboxTitleError,
    hasWarning: hasMessageInboxTitleWarning,
  });

  const messageInboxSubtitleErrorText = constructErrorInlineText({
    condition: isEditModeVisible,
    hasError: hasMessageInboxSubtitleError,
    hasWarning: hasMessageInboxSubtitleWarning,
  });

  const messageInboxIconErrorText = constructErrorInlineText({
    condition: isEditModeVisible,
    hasError: hasMessageInboxIconError,
    hasWarning: hasMessageInboxIconWarning,
  });

  return (
    <>
      {inAppData?.saveToInbox && (
        <>
          <FormField
            hasError={hasMessageInboxTitleError}
            hasWarning={hasMessageInboxTitleWarning}
            errorText={messageInboxTitleErrorText}
            warningText={messageInboxTitleErrorText}
            isHorizontalLayout
            labelText={SETTINGS_DISPLAY_CONTENT.mobileInboxTitle.labelText}
            restrictionText={getMobileInboxRestrictionText(
              inAppData.inboxMetadata?.title,
              25,
              isEditModeVisible,
            )}
          >
            <TextInputField
              hasError={hasMessageInboxTitleError}
              hasWarning={hasMessageInboxTitleWarning}
              mediumRef={titleRef}
              mediumDataKey={'inboxMetadata.title'}
              mediumDataValue={inAppData.inboxMetadata?.title || ''}
              changeHandler={(title) =>
                setCurrentMediumField('inboxMetadata', {
                  ...inAppData.inboxMetadata,
                  title,
                })
              }
              showSnippet
            />
          </FormField>
          <FormField
            hasError={hasMessageInboxSubtitleError}
            hasWarning={hasMessageInboxSubtitleWarning}
            warningText={messageInboxSubtitleErrorText}
            errorText={messageInboxSubtitleErrorText}
            isHorizontalLayout
            labelText={SETTINGS_DISPLAY_CONTENT.mobileInboxBody.labelText}
            restrictionText={getMobileInboxRestrictionText(
              inAppData.inboxMetadata?.subtitle,
              75,
              isEditModeVisible,
            )}
          >
            <TextAreaField
              hasError={hasMessageInboxSubtitleError}
              hasWarning={hasMessageInboxSubtitleWarning}
              mediumRef={bodyRef}
              mediumDataKey={'inboxMetadata.subtitle'}
              mediumDataValue={inAppData.inboxMetadata?.subtitle || ''}
              onChangeHandler={(subtitle) =>
                setCurrentMediumField('inboxMetadata', {
                  ...inAppData.inboxMetadata,
                  subtitle,
                })
              }
            />
          </FormField>
          <FormField
            isHorizontalLayout
            labelText={SETTINGS_DISPLAY_CONTENT.mobileInboxThumbnail.labelText}
            hasError={hasMessageInboxIconError}
            hasWarning={hasMessageInboxIconWarning}
            errorText={messageInboxIconErrorText}
          >
            {isEditModeVisible ? (
              <InputText
                id="settings-template-inbox-icon"
                data-test="settings-template-inbox-icon"
                onChange={(icon) =>
                  setCurrentMediumField('inboxMetadata', {
                    ...inAppData.inboxMetadata,
                    icon,
                  })
                }
                value={inAppData.inboxMetadata?.icon || ''}
                width="100%"
                hasIcon={false}
                hasError={hasMessageInboxIconError}
              />
            ) : (
              <>
                {inAppData.inboxMetadata?.icon ? (
                  <ReadOnlyContainer>
                    {inAppData.inboxMetadata?.icon && (
                      <ReadOnlyWrapper>
                        <ReadOnlyLink
                          href={validateUrl(inAppData.inboxMetadata?.icon)}
                          target="_blank"
                          rel="noreferrer noopener"
                        >
                          <LinkContent isUnderlined={true}>
                            {inAppData.inboxMetadata?.icon}
                          </LinkContent>
                          <OpenNewWindow16 color={textColors.textLink} />
                        </ReadOnlyLink>
                      </ReadOnlyWrapper>
                    )}
                  </ReadOnlyContainer>
                ) : (
                  <StyledReadOnlyText isUnset={true} data-test="icon-url-ro">
                    {SETTINGS_DISPLAY_CONTENT.unsetText}
                  </StyledReadOnlyText>
                )}
              </>
            )}
          </FormField>
        </>
      )}
    </>
  );
};
